/**
 * ProximityLayer — Sprint 1 mount point.
 * Runs app-wide while a trip is active. Loads the destination city's geolocated
 * POIs, drives the background proximity watcher (useProximity), and renders the
 * soft, dismissible "the neighbourhood whispers" notice in the Los Días voice.
 *
 * Nothing here is gamified: no points, no timers, no badges. The whisper is an
 * invitation, not a reward; the reveal itself is the reward and is silent.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import storageManager from '../utils/storageManager';
import loadTripPois from '../utils/loadTripPois';
import useProximity from '../hooks/useProximity';
import theme from '../styles/theme';

export default function ProximityLayer({ hasActiveTrip, tripVersion = 0 }) {
  const { t } = useTranslation('common');
  const [pois, setPois] = useState([]);

  useEffect(() => {
    let cancelled = false;
    if (!hasActiveTrip) { setPois([]); return; }
    (async () => {
      const trip = await storageManager.getActiveTrip().catch(() => null);
      if (cancelled || !trip) return;
      const list = await loadTripPois(trip);
      if (!cancelled) setPois(list);
    })();
    return () => { cancelled = true; };
  }, [hasActiveTrip, tripVersion]);

  // When a trip becomes active ("the trip's day begins"), make sure location is
  // requested for whispers + serendipity — but only surface the prompt if it
  // hasn't been decided yet, so we never nag users who already granted/denied.
  useEffect(() => {
    if (!hasActiveTrip || typeof navigator === 'undefined' || !('geolocation' in navigator)) return;
    let cancelled = false;
    (async () => {
      try {
        if (navigator.permissions?.query) {
          const st = await navigator.permissions.query({ name: 'geolocation' });
          if (st.state !== 'prompt') return; // already granted or denied — don't nag
        }
      } catch { /* Permissions API unavailable → fall through and ask once */ }
      if (cancelled) return;
      // One-shot request to surface the OS prompt; the live watcher takes over after.
      navigator.geolocation.getCurrentPosition(() => {}, () => {},
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 });
    })();
    return () => { cancelled = true; };
  }, [hasActiveTrip, tripVersion]);

  const { whisper, dismissWhisper, revealEvent, dismissReveal } =
    useProximity(hasActiveTrip, pois);

  // A revealed gem takes precedence — you've arrived; here's its story.
  if (revealEvent) {
    const { name, reveal } = revealEvent;
    const { quote, secret, note } = reveal || {};
    return (
      <div role="status" aria-live="polite" style={styles.wrap}>
        <div style={{ ...styles.card, ...styles.revealCard }}>
          <div style={{ ...styles.eyebrow, ...styles.revealEyebrow }}>{name}</div>
          {quote && <div style={styles.revealQuote}>“{quote}”</div>}
          {secret && (
            <div style={styles.revealRow}>
              <span style={styles.revealLabel}>{t('reveal.revealSecret')}</span>
              <span style={styles.revealText}>{secret}</span>
            </div>
          )}
          {note && (
            <div style={styles.revealRow}>
              <span style={styles.revealLabel}>{t('reveal.revealNote')}</span>
              <span style={styles.revealText}>{note}</span>
            </div>
          )}
          <button type="button" style={styles.dismiss} onClick={dismissReveal}>
            {t('reveal.whisperDismiss')}
          </button>
        </div>
      </div>
    );
  }

  if (!whisper) return null;

  return (
    <div role="status" aria-live="polite" style={styles.wrap}>
      <div style={styles.card}>
        <div style={styles.eyebrow}>{t('reveal.whisperEyebrow')}</div>
        <div style={styles.body}>{t('reveal.whisperBody', { name: whisper.name })}</div>
        <button type="button" style={styles.dismiss} onClick={dismissWhisper}>
          {t('reveal.whisperDismiss')}
        </button>
      </div>
    </div>
  );
}

const styles = {
  wrap: {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 'calc(env(safe-area-inset-bottom, 0px) + 84px)',
    display: 'flex',
    justifyContent: 'center',
    padding: '0 16px',
    zIndex: 1200,
    pointerEvents: 'none',
  },
  card: {
    pointerEvents: 'auto',
    maxWidth: 420,
    width: '100%',
    background: theme.cream,
    border: `1px solid ${theme.bluePale}`,
    borderLeft: `3px solid ${theme.blue}`,
    borderRadius: 12,
    boxShadow: '0 6px 24px rgba(26, 51, 64, 0.16)',
    padding: '14px 16px',
    animation: 'wf-whisper-in 360ms ease-out',
  },
  eyebrow: {
    fontSize: 11,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: theme.blue,
    fontWeight: 600,
    marginBottom: 4,
  },
  body: {
    fontSize: 15,
    lineHeight: 1.4,
    color: theme.brownDark,
    fontStyle: 'italic',
  },
  // ── Revealed gem (the reward) — terracotta accent ──
  revealCard: {
    borderLeft: `3px solid ${theme.terracotta}`,
    maxHeight: '52vh',
    overflowY: 'auto',
  },
  revealEyebrow: {
    color: theme.terracottaDark,
    fontSize: 14,
    letterSpacing: '0.01em',
    textTransform: 'none',
    marginBottom: 8,
  },
  revealQuote: {
    fontStyle: 'italic',
    fontSize: 15,
    lineHeight: 1.5,
    color: theme.brownDark,
    marginBottom: 8,
  },
  revealRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    marginTop: 6,
  },
  revealLabel: {
    fontSize: 11,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: theme.terracottaDark,
    fontWeight: 600,
  },
  revealText: {
    fontSize: 14,
    lineHeight: 1.5,
    color: theme.brownDark,
  },
  dismiss: {
    marginTop: 10,
    background: 'transparent',
    border: 'none',
    color: theme.blueDeep,
    fontSize: 13,
    fontWeight: 600,
    padding: 0,
    cursor: 'pointer',
  },
};
